/**
 * Chart documentation shown behind the info icon on the A/B Tests dashboard.
 *
 * Keys match the chart link property names in `MopaAbtestsComponent`.
 */
export const MopaAbtestsChartDocs = {
    // REVENUE

    arpu_uplift: /* HTML */ `<p>
            Running ARPU of each split group against the reference group, by day
            number.
        </p>
        <pre>
  group running ARPU (numerator)
  ------------------------------
  reference group running ARPU (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    arpu_daily_uplift: /* HTML */ `<p>
            Daily ARPU of each split group against the reference group, by day
            number.
        </p>
        <pre>
  group daily ARPU (numerator)
  ------------------------------
  reference group daily ARPU (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    revenue: /* HTML */ `<p>Revenue recorded on each day.</p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - revenue in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> revenue rows summed per day; no
            denominator. Net/gross and the currency follow the current revenue
            settings.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code>; valid players only.
        </p>`,

    running_revenue_by_date: /* HTML */ `<p>
            Cumulative revenue of the cohort accumulated by calendar date.
        </p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - cumulative revenue in
            the selected currency.
        </p>
        <p>
            <strong>What's counted:</strong> daily cohort revenue summed and
            accumulated from the start of the selected date range; no
            denominator. Net/gross and the currency follow the current revenue
            settings.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code>; valid players only.
        </p>`,

    running_revenue_uplift: /* HTML */ `<p>
            Running cohort revenue of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group running cohort revenue (numerator)
  ------------------------------
  reference group running cohort revenue (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (revenue),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    // CONVERSION

    running_cr_uplift: /* HTML */ `<p>
            Running conversion rate of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group running CR (numerator)
  ------------------------------
  reference group running CR (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (conversions),
            <code>bi_viz.dashboard_users_activity</code> (cohort size); valid
            players only.
        </p>`,

    purchase_rate_uplift: /* HTML */ `<p>
            Purchase rate of each split group against the reference group, by
            day number.
        </p>
        <pre>
  group purchase rate (numerator)
  ------------------------------
  reference group purchase rate (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_sales</code> (payers),
            <code>bi_viz.dashboard_users_activity</code> (active users); valid
            players only.
        </p>`,

    // RETENTION

    avg_user_lifetime: /* HTML */ `<p>
            Estimated days a user stays active, read at a given day number - the
            area under the retention curve.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - expected active days.
        </p>
        <p>
            <strong>How it's built:</strong> by-day-no retention rates summed
            from day 0 up to that day number, minus 1. The -1 drops day 0, which
            is 100% for everyone. Each rate counts only players matured to that
            day number, so young cohorts do not drag the curve down.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_retention</code>; valid players only.
        </p>`,

    avg_user_lifetime_uplift: /* HTML */ `<p>
            Average user's lifetime of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group lifetime estimate (numerator)
  ------------------------------
  reference group lifetime estimate (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_retention</code>; valid players only.
        </p>`,

    // ENGAGEMENT

    med_sess_length_by_day_no: /* HTML */ `<p>
            Median session length at each day number since install.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - session length in minutes.
        </p>
        <p>
            <strong>What's counted:</strong> the 50th percentile of
            <code>duration_minutes</code> over all sessions at that day number,
            not a per-player average - a player with many short sessions
            contributes many samples.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_engagement</code>; valid players
            only.
        </p>`,

    med_sess_length_by_day_no_uplift: /* HTML */ `<p>
            Median session length of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group median session length (numerator)
  ------------------------------
  reference group median session length (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_engagement</code>; valid players
            only.
        </p>`,

    avg_no_sessions_by_day_no: /* HTML */ `<p>
            Sessions per active user at each day number since install.
        </p>
        <pre>
  sessions played at that day number (numerator)
  ------------------------------
  distinct players active then (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - sessions per active user.
        </p>
        <p>
            Only players active at that day number are in the denominator, so
            this is per active user, not per cohort.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_engagement</code>; valid players
            only.
        </p>`,

    avg_no_sessions_by_day_no_uplift: /* HTML */ `<p>
            Sessions per active user of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group sessions per user (numerator)
  ------------------------------
  reference group sessions per user (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_engagement</code>; valid players
            only.
        </p>`,

    // PROGRESS

    running_median_progress: /* HTML */ `<p>
            Median total progress at each day number since install.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - cumulative game progress reached by the median
            player.
        </p>
        <p>
            <strong>What's counted:</strong> the 50th percentile of
            <code>progress_sum_total</code> at that day number. The table holds
            one row per player-day, so this is a median across players, one
            sample each, and the column is cumulative - the line tracks how far
            the median player has got.
        </p>
        <p>
            Only reported <code>GameProgress</code> values are included; they
            are available for players installed from 2024-08-20 on version
            2.15.2+.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    running_median_progress_uplift: /* HTML */ `<p>
            Median total progress of each split group against the reference
            group, by day number.
        </p>
        <pre>
  group median progress (numerator)
  ------------------------------
  reference group median progress (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Only reported <code>GameProgress</code> values are included; they
            are available for players installed from 2024-08-20 on version
            2.15.2+.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    running_median_playthrough_length: /* HTML */ `<p>
            Playthrough minutes accumulated up to each day number.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - accumulated playthrough minutes.
        </p>
        <p>
            <strong>What's counted:</strong> the median of
            <code>duration_min</code> is taken per day number - one row per
            player-day, so one sample per player - and those daily medians are
            then summed from day 0 up to that day: a running total of medians,
            not the median of any player's total.
        </p>
        <p>
            Every player-day has a duration, including the ones with no
            progress, so this median rests on a wider sample than the progress
            chart.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    running_median_playthrough_length_uplift: /* HTML */ `<p>
            Accumulated playthrough length of each split group against the
            reference group, by day number.
        </p>
        <pre>
  group accumulated length (numerator)
  ------------------------------
  reference group accumulated length (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. Needs
            a split by Segment and an "Uplift reference" segment; the reference
            itself stays empty. Computed in the browser, not in SQL.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    // ACTIVE USERS

    dau_by_day_no: /* HTML */ `<p>
            Players active at each day number since install.
        </p>
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - players.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> at
            that day number; the table holds exactly one row per player-day, so
            every player counts once. Unlike the calendar DAU chart, this one
            reads the A/B test progress table, not the activity table.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    dau_by_day_no_uplift: /* HTML */ `<p>
            Players active of each split group against the reference group, by
            day number.
        </p>
        <pre>
  group active players (numerator)
  ------------------------------
  reference group active players (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since install, day 0 being the
            install day; Y - difference against the reference group, in % - 0%
            means no difference.
        </p>
        <p>
            Plotted as that ratio minus 1, matched on the same day number. It
            compares group sizes, so it mostly reflects how unevenly the test
            split players. Needs a split by Segment and an "Uplift reference"
            segment; the reference itself stays empty. Computed in the browser,
            not in SQL.
        </p>
        <p>
            Source:
            <code>bi_viz.dashboard_abtests_progress_duration</code>; valid
            players only.
        </p>`,

    dau_calendary: /* HTML */ `<p>Players active on each day.</p>
        <p>
            <strong>Axes:</strong> X - date, daily; Y - active players.
        </p>
        <p>
            <strong>What's counted:</strong> distinct <code>player_id</code> per
            day, so a player counts once however many sessions they play.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_users_activity</code>; valid players
            only.
        </p>`,
} as const;
